import {signal} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, Subscription} from 'rxjs';
import {appendNewEvents, HistoryEvent} from '@app/_components/history-drawer/history-drawer.helpers';

export type HistoryFeedPage = {
    events: HistoryEvent[];
    totalCount: number;
    pageSize: number;
};

export type HistoryFeedSource = {
    fetchPage: (page: number) => Observable<HistoryFeedPage>;
    /** Returns true when the page has shown the error itself, so the feed does not offer a retry. */
    handleError?: (error: HttpErrorResponse) => boolean;
};

/** The paged event list behind an app-history-drawer: Load more, Try again, and one request at a time. */
export class HistoryFeed {
    readonly events = signal<HistoryEvent[]>([]);
    readonly hasMore = signal(false);
    readonly page = signal(1);
    readonly loading = signal(false);
    readonly loadFailed = signal(false);

    private subscription?: Subscription;

    constructor(private source: HistoryFeedSource) {
    }

    load(page: number) {
        if (page === 1) {
            this.events.set([]);
            this.hasMore.set(false);
        }
        this.page.set(page);
        this.loading.set(true);
        this.loadFailed.set(false);

        this.subscription?.unsubscribe();
        this.subscription = this.source.fetchPage(page).subscribe({
            next: response => {
                this.events.update(existing => appendNewEvents(existing, response.events));
                this.hasMore.set(page * response.pageSize < response.totalCount);
                this.loading.set(false);
            },
            error: (error: unknown) => {
                this.loading.set(false);
                if (!(error instanceof HttpErrorResponse)) {
                    console.error('A history page could not be built.', error);
                    this.loadFailed.set(true);
                    return;
                }

                this.loadFailed.set(!this.source.handleError?.(error));
            }
        });
    }

    loadMore() {
        this.load(this.page() + 1);
    }

    retry() {
        this.load(this.page());
    }

    cancel() {
        this.subscription?.unsubscribe();
    }
}
